import { useState } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Power, PowerOff, AlertTriangle, CheckCircle2, PauseCircle, Loader2,
  Clock, Server, ShieldAlert, RefreshCw, ArrowLeft,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { PageTransition } from '@/components/PageTransition'
import { useOrg } from '@/contexts/OrgContext'
import { usePermissions } from '@/hooks/usePermissions'
import { invokeEdgeOrThrow } from '@/lib/edge-functions'
import { supabase } from '@/integrations/supabase/client'
import { useEngineStatus, deriveEngineState } from '@/components/outreach/engine-status'

function formatDate(iso: string | null) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('es-MX', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
    })
  } catch {
    return iso
  }
}

export function OutreachControl() {
  const { orgId } = useOrg()
  const { outreach_admin } = usePermissions()
  const queryClient = useQueryClient()

  const { data: status, isLoading, error, refetch, isFetching } = useEngineStatus(orgId)
  const state = deriveEngineState(status)

  // Dialog state
  const [enableDialogOpen, setEnableDialogOpen] = useState(false)
  const [disableDialogOpen, setDisableDialogOpen] = useState(false)
  const [disableReason, setDisableReason] = useState('')
  const [cancelQueueOnDisable, setCancelQueueOnDisable] = useState(false)

  const orphanCount = status?.orphan_scheduled_count ?? 0

  // ── Toggle mutation (calls toggle-outreach edge fn, admin-guarded) ──────────
  const toggleMutation = useMutation({
    mutationFn: async (args: { enabled: boolean; reason?: string; cancelQueue?: boolean }) => {
      // When enabling with a backlog of orphans, the owner may opt to clear the
      // pending queue first so turning the engine on doesn't unleash a burst.
      if (args.enabled && args.cancelQueue && orgId) {
        const { error: cancelErr } = await supabase
          .from('schedules')
          .update({ status: 'canceled', last_error: 'Cancelado antes de reactivar el motor', updated_at: new Date().toISOString() })
          .eq('org_id', orgId)
          .eq('status', 'scheduled')
          .lt('scheduled_at', new Date().toISOString())
        if (cancelErr) throw new Error(`No se pudo limpiar la cola: ${cancelErr.message}`)
      }
      return invokeEdgeOrThrow<{ success: boolean; enabled: boolean }>(
        'toggle-outreach',
        { enabled: args.enabled, orgId, ...(args.reason ? { reason: args.reason } : {}) },
        { timeoutMs: 30000 },
      )
    },
    onSuccess: (_res, vars) => {
      toast.success(vars.enabled ? 'Motor de outreach encendido' : 'Motor de outreach apagado', {
        description: vars.enabled
          ? 'Los envíos correrán solo si existe un ejecutor (cron). Revisa el estado abajo.'
          : 'La cola pendiente se canceló. Ningún envío saldrá hasta reactivar.',
      })
      queryClient.invalidateQueries({ queryKey: ['outreach-engine-status', orgId] })
      queryClient.invalidateQueries({ queryKey: ['outreach-scheduled', orgId] })
      setEnableDialogOpen(false)
      setDisableDialogOpen(false)
      setDisableReason('')
      setCancelQueueOnDisable(false)
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Error al cambiar el estado del motor')
    },
  })

  const busy = toggleMutation.isPending

  // ── Access guard ────────────────────────────────────────────────────────────
  if (!outreach_admin) {
    return (
      <PageTransition className="p-8">
        <div className="max-w-md mx-auto text-center py-16">
          <ShieldAlert className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h1 className="text-lg font-semibold">Acceso restringido</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Solo un administrador puede controlar el motor de outreach.
          </p>
          <Button asChild variant="outline" size="sm" className="mt-4">
            <Link to="/settings"><ArrowLeft className="h-4 w-4 mr-1" />Volver a Settings</Link>
          </Button>
        </div>
      </PageTransition>
    )
  }

  return (
    <PageTransition className="p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Link to="/settings" className="text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <h1 className="text-2xl font-semibold tracking-tight">Control de Outreach</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1 ml-7">
              Enciende o apaga el motor que envía las cadencias automatizadas de esta organización.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>

        {/* Loading / error */}
        {isLoading ? (
          <Card><CardContent className="py-12 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando estado del motor…
          </CardContent></Card>
        ) : error ? (
          <Card className="border-red-200 dark:border-red-900"><CardContent className="py-8 text-center text-sm">
            <p className="text-red-600 dark:text-red-400 flex items-center justify-center gap-1.5">
              <AlertTriangle className="h-4 w-4" /> No se pudo leer el estado del motor de outreach.
            </p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reintentar
            </Button>
          </CardContent></Card>
        ) : (
          <>
            {/* ── Master state card ────────────────────────────────────────── */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    {state === 'on' ? <Power className="h-5 w-5 text-green-600" />
                      : <PowerOff className="h-5 w-5 text-muted-foreground" />}
                    Estado del motor
                  </CardTitle>
                  {state === 'on' ? (
                    <Badge className="bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 gap-1">
                      <CheckCircle2 className="h-3 w-3" /> ON
                    </Badge>
                  ) : state === 'enabled_no_cron' ? (
                    <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 gap-1">
                      <AlertTriangle className="h-3 w-3" /> Habilitado, sin ejecutor
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300 gap-1">
                      <PauseCircle className="h-3 w-3" /> OFF
                    </Badge>
                  )}
                </div>
                <CardDescription>
                  {state === 'on'
                    ? 'El kill switch está encendido y hay un ejecutor procesando la cola.'
                    : state === 'enabled_no_cron'
                      ? 'El kill switch está encendido pero NO existe un cron que drene la cola — nada se envía todavía.'
                      : 'El kill switch está apagado. Ningún envío se ejecuta.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Toggle actions */}
                {status?.outreach_enabled === true ? (
                  <Button
                    variant="destructive"
                    disabled={busy}
                    onClick={() => setDisableDialogOpen(true)}
                  >
                    <PowerOff className="h-4 w-4 mr-2" />
                    Apagar motor de outreach
                  </Button>
                ) : (
                  <Button
                    className="bg-green-600 hover:bg-green-700 text-white"
                    disabled={busy}
                    onClick={() => setEnableDialogOpen(true)}
                  >
                    <Power className="h-4 w-4 mr-2" />
                    Encender motor de outreach
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* ── Diagnostics card ─────────────────────────────────────────── */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Diagnóstico del ejecutor</CardTitle>
                <CardDescription>Estado real del cron y de la cola de envíos.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <DiagnosticRow
                  icon={<Server className="h-4 w-4" />}
                  label="Ejecutor (cron process-queue)"
                  value={status?.cron_exists ? 'Activo' : 'No existe'}
                  ok={status?.cron_exists === true}
                  hint={!status?.cron_exists ? 'Sin cron, aunque el switch esté ON, la cola no se procesa.' : undefined}
                />
                <DiagnosticRow
                  icon={status?.outreach_enabled ? <CheckCircle2 className="h-4 w-4" /> : <PauseCircle className="h-4 w-4" />}
                  label="Kill switch (outreach_enabled)"
                  value={status?.outreach_enabled === true ? 'Encendido' : status?.outreach_enabled === false ? 'Apagado' : 'Sin configurar'}
                  ok={status?.outreach_enabled === true}
                />
                <DiagnosticRow
                  icon={<Clock className="h-4 w-4" />}
                  label="Envíos huérfanos (en cola, vencidos)"
                  value={orphanCount.toLocaleString('es-MX')}
                  ok={orphanCount === 0}
                  hint={orphanCount > 0 ? `El más antiguo: ${formatDate(status?.oldest_orphan_at ?? null)}` : undefined}
                />
                <DiagnosticRow
                  icon={<Clock className="h-4 w-4" />}
                  label="Cadencias automatizadas activas"
                  value={(status?.automated_cadences ?? 0).toLocaleString('es-MX')}
                  ok
                />
              </CardContent>
            </Card>

            {/* Orphan burst warning */}
            {state !== 'on' && orphanCount > 0 && (
              <Card className="border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
                <CardContent className="py-4 flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-900 dark:text-amber-200">
                      Hay {orphanCount.toLocaleString('es-MX')} envíos vencidos en cola
                    </p>
                    <p className="text-amber-800 dark:text-amber-300 text-xs mt-0.5">
                      Si enciendes el motor con un ejecutor activo, todos intentarán salir de golpe. Puedes
                      cancelar la cola pendiente al encender para evitar el burst.
                    </p>
                    <Link to="/outreach" className="text-xs underline text-amber-900 dark:text-amber-200 mt-1 inline-block">
                      Revisar la cola en Outreach Activity
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* ── Enable confirmation dialog (fail-safe: explicit confirm to turn ON) ── */}
      <Dialog open={enableDialogOpen} onOpenChange={(o) => !busy && setEnableDialogOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Power className="h-5 w-5 text-green-600" /> Encender motor de outreach
            </DialogTitle>
            <DialogDescription>
              Esto habilita el envío de cadencias automatizadas para esta organización.
            </DialogDescription>
          </DialogHeader>

          {orphanCount > 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30 p-3 space-y-2">
              <p className="text-sm text-amber-900 dark:text-amber-200 flex items-center gap-1.5">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Hay {orphanCount.toLocaleString('es-MX')} envíos vencidos que intentarán salir de inmediato.
              </p>
              <label className="flex items-center gap-2 text-sm text-amber-900 dark:text-amber-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={cancelQueueOnDisable}
                  onChange={(e) => setCancelQueueOnDisable(e.target.checked)}
                  className="rounded"
                />
                Cancelar la cola pendiente antes de encender (recomendado)
              </label>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No hay envíos vencidos en cola. Puedes encender con seguridad.
            </p>
          )}

          {!status?.cron_exists && (
            <p className="text-xs text-muted-foreground flex items-start gap-1.5">
              <Server className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              Nota: actualmente no existe un cron ejecutor. Encender el switch no enviará nada hasta que
              se restaure el ejecutor.
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEnableDialogOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={busy}
              onClick={() => toggleMutation.mutate({ enabled: true, cancelQueue: cancelQueueOnDisable })}
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Power className="h-4 w-4 mr-2" />}
              Confirmar y encender
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Disable dialog (reason optional, free to turn OFF) ─────────────── */}
      <Dialog open={disableDialogOpen} onOpenChange={(o) => !busy && setDisableDialogOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PowerOff className="h-5 w-5" /> Apagar motor de outreach
            </DialogTitle>
            <DialogDescription>
              Se cancelan todos los envíos programados y no saldrá ninguno hasta reactivar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="disable-reason">Motivo (opcional)</Label>
            <Textarea
              id="disable-reason"
              value={disableReason}
              onChange={(e) => setDisableReason(e.target.value)}
              placeholder="Ej. pausa por revisión de mensajes…"
              className="text-sm min-h-[64px]"
              disabled={busy}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableDialogOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => toggleMutation.mutate({ enabled: false, reason: disableReason.trim() || undefined })}
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PowerOff className="h-4 w-4 mr-2" />}
              Apagar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageTransition>
  )
}

function DiagnosticRow({
  icon, label, value, ok, hint,
}: {
  icon: React.ReactNode
  label: string
  value: string
  ok: boolean
  hint?: string
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b last:border-b-0">
      <div className="flex items-start gap-2 min-w-0">
        <span className={ok ? 'text-green-600 dark:text-green-400 mt-0.5' : 'text-muted-foreground mt-0.5'}>
          {icon}
        </span>
        <div className="min-w-0">
          <p className="text-sm">{label}</p>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
      </div>
      <span className={`text-sm font-medium shrink-0 ${ok ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
        {value}
      </span>
    </div>
  )
}
